import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {FilterDropdownBox} from '../../../../components/common/filters/filterDropdownBox/filterDropdownBox';
import {AnalyticsMetricsKeys} from '../../../../models/api/analyticsMetricsModel';
import {getMetricLabels} from '../../../helpers/metricLabels';

/*
 * Props.
 */

export interface MetricWithOptionalRenderer {
  metric: AnalyticsMetricsKeys;
  customRender?: Renderer;
  isDisabled: boolean;
}

interface MetricSelectionDropdownProps {
  metricsWithOptionalRenderer: ReadonlyArray<MetricWithOptionalRenderer>;
  selectedMetric: AnalyticsMetricsKeys;
  setSelectedMetric: HandlerOf<AnalyticsMetricsKeys>;
}

/*
 * Styles.
 */

const StyledSimpleDropdown = styled(SimpleDropdown)`
  width: fit-content;
`;

const StyledValueDiv = styled.div`
  ${flexCenter};
`;

/*
 * Component.
 */

export const MetricSelectionDropdown: FC<MetricSelectionDropdownProps> = ({
  metricsWithOptionalRenderer,
  selectedMetric,
  setSelectedMetric,
}) => (
  <FilterDropdownBox
    renderButton={() => getMetricLabels(selectedMetric).label}
    renderDropdown={(modalProps: PopoverTransferProps) =>
      renderDropdown(modalProps, metricsWithOptionalRenderer, selectedMetric, setSelectedMetric)
    }
  />
);

/*
 * Helpers.
 */

const renderDropdown = (
  modalProps: PopoverTransferProps,
  metricsWithOptionalRenderer: ReadonlyArray<MetricWithOptionalRenderer>,
  selectedMetric: AnalyticsMetricsKeys,
  setSelectedMetric: HandlerOf<AnalyticsMetricsKeys>,
) => (
  <StyledSimpleDropdown anchor={modalProps.anchor} onRequestClose={modalProps.onRequestClose} width={180}>
    {metricsWithOptionalRenderer.map((metric) =>
      renderMetricDropdownItem(metric, selectedMetric, setSelectedMetric),
    )}
  </StyledSimpleDropdown>
);

const renderMetricDropdownItem = (
  metricAndRenderer: MetricWithOptionalRenderer,
  selectedMetric: AnalyticsMetricsKeys,
  setSelectedMetric: HandlerOf<AnalyticsMetricsKeys>,
) => {
  const {metric, customRender, isDisabled} = metricAndRenderer;

  return (
    <SimpleDropdownItem
      key={metric}
      isChecked={selectedMetric === metric}
      onSelect={() => setSelectedMetric(metric)}
      isDisabled={isDisabled}
    >
      <StyledValueDiv>
        {getMetricLabels(metric).label}
        {customRender && customRender()}
      </StyledValueDiv>
    </SimpleDropdownItem>
  );
};
